import { FC } from "react";

import { useGetUserDetailsQuery } from "@store/services/user";

import { useCart } from "@hooks/useCart";
import { useIsScrolledToBottom } from "@hooks/useIsScrolledToBottom";
import { useProducts } from "@hooks/useProducts";

import { getCartSubtotal, getLinePricing } from "@utils";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CartContentSkeleton from "./components/CartContentSkeleton";
import CartEmptyState from "./components/CartEmptyState";
import CartFooter from "./components/CartFooter";
import CartLineItem from "./components/CartLineItem";

const CartContent: FC = () => {
  const { data: userDetails } = useGetUserDetailsQuery();
  const { data: cart, isLoading: isCartLoading } = useCart();
  const { isScrolledToBottom, setListEndNode, scrollToBottom } = useIsScrolledToBottom();

  const partner = userDetails?.partners?.[0];
  const { data: nonPartnerProducts } = useProducts({ withoutPartner: true, skip: partner == null });

  if (isCartLoading || userDetails == null) {
    return <CartContentSkeleton />;
  }

  if (cart == null || cart.lines.length === 0) {
    return <CartEmptyState />;
  }

  const linePricing = getLinePricing(cart.lines, partner == null ? undefined : nonPartnerProducts);
  const totalPrice = Number(cart.totalPrice);
  const partnerSaving = getCartSubtotal(linePricing) - totalPrice;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <Stack spacing={2} sx={{ p: 3 }}>
        {linePricing.map(({ line, nonPartnerPrice }) => (
          <CartLineItem key={line.lineId} lineItem={line} nonPartnerPrice={nonPartnerPrice} cartId={cart.cartId} />
        ))}
      </Stack>
      <Box ref={setListEndNode} sx={{ height: "1px", flexShrink: 0 }} />
      <CartFooter
        totalPrice={totalPrice}
        partnerSaving={partnerSaving}
        partnerName={partner?.name}
        checkoutUrl={cart.checkoutUrl}
        isScrolledToBottom={isScrolledToBottom}
        onScrollDown={scrollToBottom}
      />
    </Box>
  );
};

export default CartContent;
